import { getWorkflowCapabilities } from '@jira/project-settings__workflow-services-capabilities/src/getWorkflowCapabilities.tsx';
import type { WorkflowCapabilities } from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
// eslint-disable-next-line jira/import-whitelist
import {
	prefetchApiReadyMark,
	prefetchApiStartMark,
} from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import { createResourceError } from '@atlassian/jira-router-resources-utils/src/utils/create-resource-error/index.tsx';
import type { ResourceStoreContext, RouterDataContext } from '@atlassian/react-resource-router';
// import type { ProjectContext } from '@atlassian/jira-router-resources-project-workflow-context/src/services/project-context/types.tsx';
import type { ProjectWorkflowContext } from '@atlassian/jira-router-resources-project-workflow-context/src/services/project-workflow-context/types.tsx';
import { consumeDependency } from '@atlassian/jira-router-resources-utils/src/utils/consume-dependency/index.tsx';
import { RESOURCE_TYPE_PROJECT_WORKFLOW_CONTEXT } from '@atlassian/jira-router-resources-project-workflow-context/src/services/project-workflow-context/constants.tsx';

export const fetchWorkflowCapabilities = async (
	routerContext: RouterDataContext,
	{ tenantContext }: ResourceStoreContext,
) => {
	const { match, dependencies } = routerContext;

	const { isAnonymous, cloudId } = tenantContext;

	if (isAnonymous) {
		throw new Error("Workflows can't be edited in anonymous mode.");
	}
	if (!match.params.workflowId && !match.params.projectKey && !match.params.issueTypeId) {
		throw new Error('Workflow ID, project Key, or issue type ID is not defined.');
	}

	try {
		// better way to monitor perf?
		setMark(prefetchApiStartMark);

		let workflowCapabilities: WorkflowCapabilities | null = null;
		if (match.params.workflowId) {
			workflowCapabilities = await getWorkflowCapabilities(cloudId, {
				workflowId: match.params.workflowId,
			});
		} else if (match.params.projectKey && match.params.issueTypeId) {
			const consumedDependency = await consumeDependency<ProjectWorkflowContext | null>(
				dependencies,
				RESOURCE_TYPE_PROJECT_WORKFLOW_CONTEXT,
			);
			if (consumedDependency) {
				workflowCapabilities = await getWorkflowCapabilities(cloudId, {
					projectId: String(consumedDependency.id),
					issueTypeId: match.params.issueTypeId,
				});
			} else {
				throw new Error('Project workflow context is not defined.');
			}
		}
		if (!workflowCapabilities) {
			throw new Error('Workflow ID, project ID, or issue type ID is not defined.');
		}

		setMark(prefetchApiReadyMark);
		return workflowCapabilities;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		throw createResourceError(
			{
				component: 'spa.view.routes.admin-pages.workflows.workflow-capabilities.getData',
				message: 'An error was thrown while prefetching workflow capabilities data',
				name: 'ResourceError',
			},
			error,
		);
	}
};

export const fetchWorkflowCapabilitiesOld = async (
	{ match }: RouterDataContext,
	{ tenantContext }: ResourceStoreContext,
) => {
	const { isAnonymous, cloudId } = tenantContext;

	if (isAnonymous || !match.params.workflowId) {
		const message = !match.params.workflowId
			? 'Workflow ID is not defined.'
			: "Workflows can't be edited in anonymous mode.";
		throw new Error(message);
	}

	try {
		// better way to monitor perf?
		setMark(prefetchApiStartMark);
		const workflowCapabilities = await getWorkflowCapabilities(cloudId, {
			workflowId: match.params.workflowId,
		});
		setMark(prefetchApiReadyMark);
		return workflowCapabilities;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		throw createResourceError(
			{
				component: 'spa.view.routes.admin-pages.workflows.workflow-capabilities.getData',
				message: 'An error was thrown while prefetching workflow capabilities data',
				name: 'ResourceError',
			},
			error,
		);
	}
};
